import { mockFederatedGraphql } from 'garaje/mirage/helpers/mock-graphql';
import { mirageGraphQLFieldResolver } from '@miragejs/graphql';
import config from 'garaje/config/environment';
import { differenceInDays, addDays, parseISO } from 'date-fns';
import { capitalize } from '@ember/string';

export default function () {
  this.urlPrefix = config.apiHost;

  mockFederatedGraphql(this, {
    Query: {
      assignments(_obj, _args, _context) {
        return [];
      },
      featureConfig(_obj, _args, _context) {
        return {};
      },
      assignedAndUnassignedEmployees(_obj, _args, _context) {
        return {};
      },
      areaMap(_obj, _args, _context) {
        return {};
      },
      availableDesksForLocation(obj, args, context, info) {
        const { mirageSchema } = context;

        const floor = mirageSchema.create(`${obj.prefix}/floor`, {
          name: 'Floor 1',
          building: 'Building 1',
          floorPlanUrl:
            'https://envoy-desks-prod.s3.amazonaws.com/maps/95026/122890/ec46a9a4ceddd320c71c787e762758f0c084ce037a76cb34672bf404-1612823218-SF',
        });
        const floor2 = mirageSchema.create(`${obj.prefix}/floor`, {
          name: 'Floor 2',
          building: 'Building 1',
          floorPlanUrl:
            'https://envoy-desks-prod.s3.amazonaws.com/maps/95026/122890/ec46a9a4ceddd320c71c787e762758f0c084ce037a76cb34672bf404-1617986255-SLT',
        });

        server.create('graphql/desk', {
          floor,
          deskAvailability: 'userAssigned',
        });
        server.createList('graphql/desk', 10, {
          floor,
        });
        server.createList('graphql/desk', 10, {
          floor: floor2,
        });

        return mirageSchema['graphql/desks'].all().models;
      },
      desksOnFloorForLocation(obj, args, context, info) {
        const { mirageSchema } = context;
        const floor = mirageSchema.create(`${obj.prefix}/floor`, {
          name: 'Floor 1',
          building: 'Building 1',
          floorPlanUrl:
            'https://envoy-desks-prod.s3.amazonaws.com/maps/95026/122890/ec46a9a4ceddd320c71c787e762758f0c084ce037a76cb34672bf404-1612823218-SF',
        });
        const floor2 = mirageSchema.create(`${obj.prefix}/floor`, {
          name: 'Floor 2',
          building: 'Building 1',
          floorPlanUrl:
            'https://envoy-desks-prod.s3.amazonaws.com/maps/95026/122890/ec46a9a4ceddd320c71c787e762758f0c084ce037a76cb34672bf404-1617986255-SLT',
        });

        server.create('graphql/desk', {
          floor,
          deskAvailability: 'userAssigned',
        });

        const amenities = server.createList('graphql/amenity', 3);
        server.createList('graphql/desk', 10, { floor, amenities });
        server.createList('graphql/desk', 10, { floor: floor2, amenities });
        const desks = mirageSchema['graphql/desks'].all();
        const errors = server.createList('graphql/envoy-error', 1, {
          code: 1,
          domain: 'test',
        });
        return server.create('graphql/desks-on-floor', { desks, errors });
      },
      employeeRegistration(obj, args, context, info) {
        const startDate = parseISO(args.startDate);
        const endDate = parseISO(args.endDate);
        const length = differenceInDays(endDate, startDate) + 1;
        const registrationDates = [...Array(length).keys()].map((i) => {
          return server.create(
            'graphql/registration-date',
            { date: addDays(startDate, i) },
            i > 2 ? 'withSelfCertify' : '',
            i === 2 ? 'withDeskReservation' : '',
            i === 2 ? 'withInvite' : '',
            i === 1 ? 'withDeniedInvite' : '',
            i === 0 ? 'withRegisteredInvite' : ''
          );
        });
        // use an existing desk on an existing floor, required for map to load correctly
        const currentDesk = context.mirageSchema['graphql/desks'].first();
        registrationDates.map((rd) => {
          if (rd.reservation && currentDesk) {
            rd.reservation.update('desk', currentDesk);
          }
        });

        const employeeRegistration = server.create('graphql/employee-registration', {
          id: 1,
          registrationDates,
        });
        return employeeRegistration;
      },
      employeeRegistrationPartialDay(obj, args, context, info) {
        const startDate = parseISO(args.startDate);
        const endDate = parseISO(args.endDate);
        const length = differenceInDays(endDate, startDate) + 1;
        const registrationDates = [...Array(length).keys()].map((i) => {
          return server.create(
            'graphql/registration-date-partial-day',
            { date: addDays(startDate, i) },
            i > 3 ? 'withSelfCertify' : '',
            i === 3 ? 'withAllDayReservation' : '',
            i === 2 ? 'withDeskReservations' : '',
            i === 2 ? 'withInvite' : '',
            i === 1 ? 'withDeniedInvite' : '',
            i === 0 ? 'withRegisteredInvite' : ''
          );
        });
        // use an existing desk on an existing floor, required for map to load correctly
        const currentDesk = context.mirageSchema['graphql/desks'].first();
        registrationDates.map((rd) => {
          if (rd.reservation && currentDesk) {
            rd.reservation.update('desk', currentDesk);
          }
        });

        const employeeRegistration = server.create('graphql/employee-registration-partial-day', {
          id: 1,
          registrationDates,
        });
        return employeeRegistration;
      },
      reservationById(_obj, args, context) {
        const reservation = context.mirageSchema['graphql/reservations'].find(args.reservationId);
        return reservation ?? server.create('graphql/reservation');
      },
    },
    Mutation: {
      changeDeskReservation(obj, args, context, info) {
        const { currentDeskId, newDeskId, reservationId } = args;
        return {};
      },
      createInviteReservation(obj, args, context, info) {
        const { deskId, invite, inviteId } = args;
        if (deskId) {
          return server.create('graphql/reservation-response');
        } else {
          return server.create('graphql/reservation-response', { reservationId: null });
        }
      },
      releaseDeskReservation(obj, args, context, info) {
        server.schema['graphql/reservations'].find(args.reservationId).destroy();
        return { message: 'Desk released', success: true };
      },
      releaseDeskReservations(obj, args, context, info) {
        args.reservationIds.forEach((id) => {
          server.schema['graphql/reservations'].find(id).destroy();
        });
        return { message: 'Desk released', success: true };
      },
      deleteInvite(obj, args, context, info) {
        const { id } = args;

        return { id };
      },
      deleteInviteReservation(obj, args, context, info) {
        const { id } = args;

        return { id };
      },
      signInInvite(obj, args, context, info) {
        const { inviteId } = args;
        const entry = server.create('graphql/entry');

        server.schema['graphql/invites'].find(inviteId)?.registrationDate.update({ entry });

        return [entry];
      },
      signOutEntry(obj, args, context, info) {
        const { id } = args;

        // we need this to be a signed out entry. needs a trait
        return server.create('graphql/entry');
      },
    },
    RegistrationDate: {
      screeningCard(obj, args, context, info) {
        info.fieldName = 'screeningCardable';
        info.path.key = 'screeningCardable';
        const screeningCard = mirageGraphQLFieldResolver(obj, args, context, info);

        // we need to strip our mirage namespace and coerce to the graphql type
        screeningCard.__typename = capitalize(screeningCard.__typename.split('Graphql/')[1]);
        return screeningCard;
      },
    },
    RegistrationDatePartialDay: {
      screeningCard(obj, args, context, info) {
        info.fieldName = 'screeningCardable';
        info.path.key = 'screeningCardable';
        const screeningCard = mirageGraphQLFieldResolver(obj, args, context, info);

        // we need to strip our mirage namespace and coerce to the graphql type
        screeningCard.__typename = capitalize(screeningCard.__typename.split('Graphql/')[1]);
        return screeningCard;
      },
    },
  });
}
